import { useRef, useState, useEffect } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'

import { SearchInput } from 'libs/searchspring/components/quick-search/SearchInput'
import { QuickSearchDropdown } from 'libs/searchspring/components/quick-search/QuickSearchDropdown'
import { SearchspringAutocompleteProvider } from 'libs/searchspring/fetchers/autocomplete'

function QuickSearch({ id = 'quick-search' }) {
  const searchInputRef = useRef()
  const [inputValue, setInputValue] = useState()
  const [query, setQuery] = useState()
  const closeButtonRef = useRef()
  const modalRef = useRef()
  const [redirect, setRedirect] = useState(false)
  const searchUrl =
    (typeof window !== 'undefined' && window.globals?.routes?.search_url) ||
    (typeof window !== 'undefined' && window.Shopify?.routes?.root ? `${window.Shopify.routes.root}search` : '/search')

  const handleClose = (event) => {
    event.preventDefault()
    modalRef.current?.handleHide()
  }

  const handleUpdateValue = (event) => {
    setInputValue(event.target.value)
  }

  const resetInputValue = () => {
    setInputValue('')
    searchInputRef.current.value = ''
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(inputValue)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [inputValue])

  useEffect(() => {
    const modalEl = modalRef.current
    if (!modalEl) return

    const focusInput = () => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }

    if (modalEl.dataset.ui === 'visible') focusInput()

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ui') {
          if (modalEl.dataset.ui === 'visible') focusInput()
        }
      }
    })

    observer.observe(modalEl, { attributes: true, attributeFilter: ['data-ui'] })

    const triggers = document.querySelectorAll(
      `[data-modal-parent~="${id}"][data-modal-role~="open"], [data-modal-parent~="${id}"][data-modal-role~="toggle"]`
    )

    const handleTriggerClick = (event) => {
      const role = event.currentTarget.getAttribute('data-modal-role') || ''
      const isToggle = role.split(/\s+/).includes('toggle')
      const willOpen = !isToggle || modalEl.dataset.ui !== 'visible'
      if (willOpen) searchInputRef.current?.focus()
    }

    triggers.forEach((trigger) => trigger.addEventListener('click', handleTriggerClick, true))

    return () => {
      observer.disconnect()
      triggers.forEach((trigger) => trigger.removeEventListener('click', handleTriggerClick, true))
    }
  }, [id])

  return (
    <x-modal className="group/search flex items-center h-full z-0" data-id={id} data-body-lock ref={modalRef}>
      <div className="flex flex-col fixed inset-tl w-full h-fit z-100 -translate-y-full group-data-visible/search:translate-y-0 transition-all">
        <form action={searchUrl} method="get" className="flex items-center justify-center w-full h-fit lg-max:pr-0 px-margin py-0.25 lg:py-1.5 border-b border-borders-default bg-secondary">
          <div className="flex items-center gap-x-0.75 w-full h-full max-w-[548px] lg:pb-0.125 lg:border-b lg:border-borders-default">
            <div className="shrink-0 flex size-[13px] children:size-full">
              <Icon id="magnifying-glass" />
            </div>
            <SearchInput className="grow" ref={searchInputRef} redirect={redirect} onInput={handleUpdateValue} />
            <div className="shrink-0 flex items-center justify-end">
              {inputValue ? (
                <button className="btn btn-text btn-tertiary btn-m w-fit" onClick={resetInputValue} type="reset" aria-label="Clear search">
                  <span className="text-line text-line-large">
                    Clear
                  </span>
                </button>
              ) : null}
              <button
                className="btn btn-transparent btn-primary btn-m btn-square"
                data-modal-parent={id}
                data-modal-role="close"
                ref={closeButtonRef}
                onClick={handleClose}
                aria-label="Close modal"
              >
                <div className="size-[10px] children:size-full">
                  <Icon id="close" />
                </div>
              </button>
            </div>
          </div>
        </form>
        <SearchspringAutocompleteProvider runOnMount={false} resultsPerPage={4}>
          <QuickSearchDropdown query={query} redirect={redirect} setRedirect={setRedirect} />
        </SearchspringAutocompleteProvider>
      </div>
      <button
        className="fixed inset-tl size-full bg-type-black-primary/60 opacity-0 group-data-visible/search:opacity-100 pointer-events-none group-data-visible/search:pointer-events-auto cursor-n-resize z-90 transition-all"
        data-modal-parent={id}
        data-modal-role="close"
        aria-label="Close modal"
      ></button>
    </x-modal>
  )
}

export function WrappedQuickSearch({ id }) {
  return (
    <SearchspringAutocompleteProvider runOnMount={false}>
      <QuickSearch id={id} />
    </SearchspringAutocompleteProvider>
  )
}
